import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  QaiFlexContainerComponent,
  QaiCallInsightsCardComponent,
  QaiProgressBarComponent,
} from '@qai/ui';
import { ActivatedRoute } from '@angular/router';
import { ReportingDashboardService } from 'src/app/services/reporting-dashboard/reporting-dashboard.service';
import { ReportingLocalStorageUtil } from 'src/app/utils/reporting-localstorage.util';
import { IModuleAssignmentHighlightCard } from 'src/app/interfaces/reportingV2/imodule-assignment-highlight-card';
import {
  HIGHLIGHT_CARD_TYPES,
} from 'src/app/utils/constants/reporting-dashboard/highlight-card.constants';
import { TranslationService } from 'src/app/services/translation.service';
import { TranslocoModule } from '@jsverse/transloco';
import { AppConstants } from 'src/app/app-constants';
import { withDemoModule } from 'src/app/utils/demo-mode/demo-mode-context.util';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { CommonModule } from '@angular/common';
import { HighlightCardMappingUtil } from 'src/app/utils/highlight-card-mapping.util';
import { IInsightCard } from 'src/app/interfaces/reportingV2/iinsight-card';

@Component({
  selector: 'app-highlight-cards',
  templateUrl: './highlight-cards.component.html',
  styleUrls: ['./highlight-cards.component.scss'],
  standalone: true,
  imports: [
    QaiFlexContainerComponent,
    QaiCallInsightsCardComponent,
    QaiProgressBarComponent,
    TranslocoModule,
    CommonModule
  ],
})
export class HighlightCardsComponent implements OnInit {
  insightCards: IInsightCard[] = [];
  isLoading = false;
  showHighlightCards = false;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private route: ActivatedRoute,
    private reportingDashboardService: ReportingDashboardService,
    private translationService: TranslationService,
    private featureFlagsService: FeatureFlagsService
  ) {}

  async ngOnInit(): Promise<void> {
    this.showHighlightCards = await this.featureFlagsService.isFlagEnabled(
      AppConstants.REPORTING_ASSIGNMENT_HIGHLIGHTS
    );
    if (!this.showHighlightCards) return;

    this.translationService.loadScopedTranslation('reportingV2').then(() => {
      const accountUuid = ReportingLocalStorageUtil.getStoredAccountUuid();
      const moduleAssignmentUuid = this.route.snapshot.paramMap.get('id');

      if (!accountUuid || !moduleAssignmentUuid) {
        console.warn('Missing accountUuid or moduleAssignmentUuid for highlight cards');
        return;
      }

      this.isLoading = true;
      this.reportingDashboardService
        .getModuleAssignmentHighlightCards({ accountUuid, moduleAssignmentUuid, teamUuids: [] }, withDemoModule(AppConstants.NEW_REPORTING_UI))
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (cards) => {
            this.insightCards = this.mapToInsightCards(cards);
            this.isLoading = false;
          },
          error: (error) => {
            console.error('Error loading highlight cards:', error);
            this.isLoading = false;
          },
        });
    });
  }

  private mapToInsightCards(cards: IModuleAssignmentHighlightCard[]): IInsightCard[] {
    return cards.map((card, index) => {
      const isMethodology =
        card.cardType === HIGHLIGHT_CARD_TYPES.HIGHEST_SCORING_METHODOLOGY ||
        card.cardType === HIGHLIGHT_CARD_TYPES.LOWEST_SCORING_METHODOLOGY;
      const max = isMethodology ? 5 : 100;
      const labelVariant: 'percentage' | 'value-max' = isMethodology ? 'value-max' : 'percentage';
      const segments = isMethodology ? 5 : 1;
      const heading = this.translationService.translate(HighlightCardMappingUtil.getHeadingKey(card.cardType), 'reportingV2');

      console.log('Mapping card:', card, 'to insight card with heading:', heading);
      const isNegative = card.averageValue < 0;
      const absValue = Math.abs(card.averageValue);
      const progressDescription = this.getProgressDescription(card.cardType, isNegative);

      return {
        id: `${card.cardType}_${index}`,
        heading,
        description: card.themeTitle ?? '',
        headingMode: 'small',
        descriptionMode: 'simple',
        showLink: false,
        subHeading: card.callName ?? undefined,
        subheadingIcon: card.callName ? 'phone-call' : undefined,
        progressBar: {
          value: absValue,
          max,
          kind: this.getProgressBarKind(card),
          shape: 'round',
          size: 'medium',
          label: String(Math.round(absValue)),
          labelVariant,
          description: progressDescription,
          segments,
        },
      };
    });
  }

  private getProgressDescription(cardType: string, isNegative: boolean): string {
    const key = HighlightCardMappingUtil.getAssignmentDescriptionKey(cardType, isNegative);
    return key ? this.translationService.translate(key, 'reportingV2') : '';
  }

    private getProgressBarKind(
      card: IModuleAssignmentHighlightCard
    ): 'success' | 'danger' | 'warning' | 'info' {
      console.log('Determining progress bar kind for card:', card);
      switch (card.cardType) {
        case HIGHLIGHT_CARD_TYPES.LEAST_MISSED_QUESTION:
        case HIGHLIGHT_CARD_TYPES.HIGHEST_SCORING_METHODOLOGY:
        case HIGHLIGHT_CARD_TYPES.LEAST_MISSED_ANSWER_POINT:
        case HIGHLIGHT_CARD_TYPES.MOST_ARTICULATED_MESSAGE:
          return 'success';
        case HIGHLIGHT_CARD_TYPES.MOST_MISSED_QUESTION:
        case HIGHLIGHT_CARD_TYPES.LOWEST_SCORING_METHODOLOGY:
        case HIGHLIGHT_CARD_TYPES.LEAST_ARTICULATED_MESSAGE:
        case HIGHLIGHT_CARD_TYPES.MOST_MISSED_ANSWER_POINT:
          return 'danger';
        default:
          return 'info';
      }
    }
}
